import { useEffect, useState } from 'react';
import type { RaRecord } from 'react-admin';
import { Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import { fmtDate } from '../shared/Panel';

/**
 * Podgląd wiadomości: treść i załączniki (PDF/obraz z S3).
 *
 * Wyjęte bez zmian z `EmailShow` (07.10.2026), żeby karta prezlecenia
 * pokazywała źródło tym samym podglądem co skrzynka — wzór „treść obok
 * formatki” z dokumentów kierowców w FreightFusion, nie nowy komponent.
 */

const API = process.env.REACT_APP_NEST_API_URL || 'http://localhost:3001';

// —— podgląd załącznika (blob z API z tokenem, jak PagePreview w FF) ——
const AttachmentPreview = ({ emailId, att }: { emailId: number; att: RaRecord }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let objectUrl: string | null = null;
    const token = localStorage.getItem('token');
    fetch(`${API}/emails/${emailId}/attachments/${att.id as number}/file`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('load'))))
      .then((b) => {
        objectUrl = URL.createObjectURL(b);
        setUrl(objectUrl);
      })
      .catch(() => setErr(true));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [emailId, att.id]);

  if (err) return <Typography color="error">Nie udało się załadować pliku</Typography>;
  if (!url) return <Typography color="text.secondary">Ładowanie…</Typography>;
  const isImg = String(att.contentType ?? '').startsWith('image/');
  return isImg ? (
    <img src={url} alt={att.filename as string} style={{ maxWidth: '100%', maxHeight: 620, border: '1px solid #ddd' }} />
  ) : (
    <iframe src={url} title={`att-${att.id as number}`} style={{ width: '100%', height: 620, border: '1px solid #ddd' }} />
  );
};

/**
 * Przełącznik Treść / załączniki + podgląd.
 *
 * `attachmentsAllowed` — pliki załączników serwuje skrzynka, a ta należy do
 * przyjęcia zleceń (`ORDER_INTAKE`, 01.10.2026). Bez uprawnienia nie
 * pobieramy pliku, który i tak skończyłby się odmową i komunikatem „nie
 * udało się załadować”, tylko mówimy wprost, czemu podglądu nie ma.
 */
export const EmailPreview = ({
  record,
  attachmentsAllowed = true,
}: {
  record: RaRecord;
  attachmentsAllowed?: boolean;
}) => {
  const attachments = (record.attachments as RaRecord[] | undefined) ?? [];
  const firstPdf = attachmentsAllowed
    ? attachments.find((a) => String(a.contentType ?? '').includes('pdf'))
    : undefined;
  const [sel, setSel] = useState<string>(firstPdf ? `att-${firstPdf.id as number}` : 'body');
  const bodyText = (record.bodyText as string | null) ?? '';
  const srcDoc =
    (record.bodyHtml as string | null) ||
    `<pre style="font-family:system-ui,sans-serif;white-space:pre-wrap;margin:0">${bodyText.replace(/</g, '&lt;')}</pre>`;
  const selAtt = attachments.find((a) => `att-${a.id as number}` === sel);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6">{(record.subject as string) || '(brak tematu)'}</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Od: {record.fromName as string} &lt;{record.fromAddress as string}&gt; · {fmtDate(record.receivedAt as string)}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }} useFlexGap>
          {/* Kontrakt `data-testid` paczki (S-22.10-02): zakładki źródeł — treść maila i załączniki (bez kartoteki). */}
          <Chip
            size="small"
            label="Treść"
            color={sel === 'body' ? 'primary' : 'default'}
            variant={sel === 'body' ? 'filled' : 'outlined'}
            onClick={() => setSel('body')}
            data-testid="cs-zlecenie-zrodla-zakladka-body"
          />
          {attachments.map((a) => (
            <Chip
              key={a.id as number}
              size="small"
              label={a.filename as string}
              color={sel === `att-${a.id as number}` ? 'primary' : 'default'}
              variant={sel === `att-${a.id as number}` ? 'filled' : 'outlined'}
              onClick={attachmentsAllowed ? () => setSel(`att-${a.id as number}`) : undefined}
              data-testid={`cs-zlecenie-zrodla-zakladka-att-${a.id as number}`}
            />
          ))}
        </Stack>
        {!attachmentsAllowed && attachments.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Podgląd załączników jest dostępny dla osób przyjmujących zlecenia.
          </Typography>
        )}
        <Divider sx={{ mb: 1 }} />
        {sel === 'body' || !selAtt ? (
          <iframe
            title="email-body"
            srcDoc={srcDoc}
            sandbox=""
            style={{ width: '100%', height: 620, border: '1px solid #e0e0e0', borderRadius: 4, background: '#fff' }}
          />
        ) : (
          <AttachmentPreview emailId={record.id as number} att={selAtt} />
        )}
      </CardContent>
    </Card>
  );
};
